import { claimSignOut, isSessionDead, registerTokenRefresh } from '@/model/client';
import { logger as SentryLogger } from '@/shared/helpers/logger';
import { useAppDispatch } from '@/shared/hooks/useStore';
import { toError, useFetchUser } from '@/viewmodel/features/user/hooks/useFetchUser';
import { logOut, setApiToken, setStatus, setUser } from '@/viewmodel/features/user/state/userSlice';
import { useLogto } from '@logto/react';
import { useCallback, useEffect, useRef } from 'react';

export const useUserData = () => {
  const dispatch = useAppDispatch();
  const {
    isAuthenticated,
    getIdTokenClaims,
    isLoading: isLogtoLoading,
    getAccessToken,
    signOut,
  } = useLogto();

  const initializedRef = useRef(false);

  const forceSignOut = useCallback(() => {
    if (!claimSignOut()) return;

    initializedRef.current = false;
    dispatch(logOut());
    signOut(`${import.meta.env.VITE_APP_URL}/`);
  }, [dispatch, signOut]);

  const resetInitialized = useCallback(() => {
    initializedRef.current = false;
  }, []);
  const { fetchUser, abortFetch } = useFetchUser(forceSignOut, resetInitialized);

  const handleLogout = useCallback(() => {
    abortFetch();
    initializedRef.current = false;
    dispatch(logOut());
  }, [dispatch, abortFetch]);

  const fetchAccessToken = useCallback(async () => {
    try {
      const accessToken = await getAccessToken(import.meta.env.VITE_LOGTO_AUDIENCE);
      if (accessToken) {
        dispatch(setApiToken(accessToken));
        registerTokenRefresh(
          getAccessToken,
          import.meta.env.VITE_LOGTO_AUDIENCE ?? '',
          (token) => dispatch(setApiToken(token)),
          forceSignOut,
        );
      }
    } catch (err: unknown) {
      SentryLogger.warn('useUserData: could not get API access token', {
        error: toError(err),
        log_source: 'useUserData',
      });
    }
  }, [dispatch, getAccessToken, forceSignOut]);

  const initializeUser = useCallback(async () => {
    if (initializedRef.current || isSessionDead()) return;
    initializedRef.current = true;
    dispatch(setStatus('loading'));

    try {
      const claims = await getIdTokenClaims();

      if (!claims?.sub) {
        SentryLogger.warn('useUserData: ID token arrived without a sub', {
          log_source: 'useUserData',
        });

        if (isAuthenticated) {
          forceSignOut();
        } else {
          dispatch(setUser(null));
          dispatch(setStatus('ready'));
        }
        return;
      }

      await fetchAccessToken();
      await fetchUser(claims.sub);
    } catch (error) {
      SentryLogger.error('useUserData: error fetching claims', {
        error: toError(error),
        log_source: 'useUserData',
      });
      if (isAuthenticated) {
        forceSignOut();
      } else {
        dispatch(setUser(null));
        dispatch(setStatus('ready'));
      }
    }
  }, [dispatch, getIdTokenClaims, fetchAccessToken, fetchUser, isAuthenticated, forceSignOut]);

  useEffect(() => {
    if (isLogtoLoading) return;
    if (isAuthenticated) {
      initializeUser();
    } else if (initializedRef.current) {
      handleLogout();
    }
  }, [isAuthenticated, isLogtoLoading, initializeUser, handleLogout]);

  return { isAuthenticated };
};
